import { isCancelledOrder } from '@comforthub/orders'
import { api } from '../api'
import {
  DEFAULT_WORK_SLIP_FIELDS,
  printOrderWorkSlip,
  type WorkSlipOrder,
  type WorkSlipSettings,
} from '../orderWorkSlip'
import type { DevicePrinterSettings, PrintMethod } from './deviceSettings'
import { sendToRawBt, type RawBtRoute } from './rawbt'
import { renderSlipJob } from './renderSlip'
import { buildSlipLines, type ReceiptOrder, type SlipLine } from './slipLayout'

/** One way to print an order, for the drawer button and auto-print alike. */

export const FALLBACK_WORK_SLIP_SETTINGS: WorkSlipSettings = {
  headerText: null,
  logoUrl: null,
  fields: DEFAULT_WORK_SLIP_FIELDS,
}

/**
 * The company's slip settings. Store staff may not be allowed to read company
 * settings (company:read); the slip then prints with the defaults rather than
 * not at all.
 */
export async function loadWorkSlipSettings(): Promise<WorkSlipSettings> {
  try {
    const res = await api.get<WorkSlipSettings>('/company/work-slip-settings')
    return res.data ?? FALLBACK_WORK_SLIP_SETTINGS
  } catch {
    return FALLBACK_WORK_SLIP_SETTINGS
  }
}

export async function loadOrderForSlip(orderId: string): Promise<ReceiptOrder> {
  const res = await api.get<ReceiptOrder>(`/orders/${orderId}`)
  if (!res.data) throw new Error("Could not load this order's details")
  return res.data
}

export type PrintResult = { method: 'dialog' } | { method: 'rawbt'; route: RawBtRoute }

export async function printReceipt(
  order: ReceiptOrder,
  settings: WorkSlipSettings,
  method: PrintMethod,
  deps: { send?: (job: Uint8Array) => Promise<RawBtRoute>; render?: (lines: SlipLine[]) => Uint8Array } = {},
): Promise<PrintResult> {
  if (method === 'dialog') {
    printOrderWorkSlip(order as WorkSlipOrder, settings)
    return { method: 'dialog' }
  }
  const job = (deps.render ?? renderSlipJob)(buildSlipLines(order, settings))
  const route = await (deps.send ?? sendToRawBt)(job)
  return { method: 'rawbt', route }
}

/** A short slip that says the printer and RawBT work, without an order. */
export function testSlipLines(now: Date = new Date()): SlipLine[] {
  return [
    { kind: 'text', text: 'ComfortHub', size: 'lg', bold: true, align: 'center' },
    { kind: 'text', text: 'Printer test', size: 'xl', bold: true, align: 'center' },
    { kind: 'rule' },
    { kind: 'text', text: 'Täpitähed: õ ä ö ü š ž Õ Ä Ö Ü Š Ž' },
    { kind: 'text', text: 'Кириллица: заказ готов' },
    { kind: 'text', text: `Sent ${now.toLocaleString('et-EE')}`, size: 'sm' },
    { kind: 'rule' },
  ]
}

export async function printTestSlip(
  deps: { send?: (job: Uint8Array) => Promise<RawBtRoute>; render?: (lines: SlipLine[]) => Uint8Array } = {},
): Promise<RawBtRoute> {
  const job = (deps.render ?? renderSlipJob)(testSlipLines())
  return (deps.send ?? sendToRawBt)(job)
}

/** What auto-print needs to know about an order from the list. */
export interface AutoPrintCandidate {
  id: string
  placedAt?: string | null
  storeId?: string | null
  paymentStatus?: string | null
}

/**
 * The orders auto-print should print now, oldest first:
 * placed (an unpaid card checkout has no placedAt yet), placed after auto-print
 * was turned on, in one of this device's stores (or any, when none are chosen),
 * not cancelled, and not printed on this device before.
 */
export function pickOrdersToAutoPrint(
  orders: readonly AutoPrintCandidate[],
  device: Pick<DevicePrinterSettings, 'autoPrint' | 'autoPrintSince' | 'storeIds'>,
  printed: ReadonlySet<string>,
): AutoPrintCandidate[] {
  if (!device.autoPrint || !device.autoPrintSince) return []
  const since = Date.parse(device.autoPrintSince)
  if (!Number.isFinite(since)) return []
  const stores = new Set(device.storeIds)
  return orders
    .filter((o) => {
      if (!o.id || printed.has(o.id)) return false
      if (!o.placedAt) return false
      const placed = Date.parse(o.placedAt)
      if (!Number.isFinite(placed) || placed < since) return false
      if (isCancelledOrder(o)) return false
      if (stores.size > 0 && !(o.storeId && stores.has(o.storeId))) return false
      return true
    })
    .sort((a, b) => Date.parse(a.placedAt!) - Date.parse(b.placedAt!))
}
